// #tag: SVG BRAND ILLUSTRATION COMPONENT
// #purpose: Renders theme-responsive vector illustrations with lazy loading, intersection observation, and dynamic accent color injection.
import React, { useEffect, useRef, useState } from 'react';
import {
  getIllustrationAssetUrl,
  isEagerIllustrationContext,
  resolveIllustrationContext,
} from '../../config/illustrations';

export type IllustrationSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface IllustrationProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  context: string;
  size?: IllustrationSize;
  alt?: string;
  className?: string;
  accentColor?: string;
  loading?: 'lazy' | 'eager';
}

const svgCache: Record<string, string> = {};

async function fetchSvg(url: string): Promise<string | null> {
  if (svgCache[url]) return svgCache[url];
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const text = await res.text();
    if (text.includes('<svg')) {
      svgCache[url] = text;
      return text;
    }
  } catch {
    /* network error */
  }
  return null;
}

// #tag: SVG ACCENT COLOR INJECTOR
// #purpose: Dynamically replaces default theme fill colors with the active WebFoxx brand accent.
function applyBrandAccent(raw: string, accentColor: string): string {
  return raw
    .replace(/var\(--primary-svg-color\)/gi, accentColor)
    .replace(/#6c63ff/gi, accentColor)
    .replace(/#6366f1/gi, accentColor)
    .replace(/<svg /i, '<svg width="100%" style="width:100%;height:auto;max-height:100%;display:block;" ');
}

// #tag: CONTEXTUAL VECTOR ILLUSTRATION
// #purpose: Dynamically resolves and mounts SVG artwork mapped to specific UI empty states and headers.
export function Illustration({
  context,
  size = 'md',
  alt,
  className = '',
  accentColor = 'var(--color-primary)',
  loading,
  style,
  ...rest
}: IllustrationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState('');
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);

  const entry = resolveIllustrationContext(context);
  const shouldEagerLoad = loading === 'eager' || (loading !== 'lazy' && isEagerIllustrationContext(context));

  useEffect(() => {
    if (shouldEagerLoad) {
      setIsVisible(true);
      return;
    }

    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldEagerLoad]);

  useEffect(() => {
    if (!isVisible || !entry) return;

    let mounted = true;
    setLoadState('loading');

    async function load() {
      const url = getIllustrationAssetUrl(entry!.file);
      const text = await fetchSvg(url);
      if (mounted) {
        setSvgContent(text ? applyBrandAccent(text, accentColor) : '');
        setLoadState(text ? 'loaded' : 'error');
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [isVisible, entry?.file, accentColor]);

  const label = alt ?? entry?.alt;
  const sizeClass = `wfx-illustration--${size}`;
  const isLoading = loadState === 'idle' || loadState === 'loading';

  return (
    <div
      ref={containerRef}
      className={`wfx-illustration ${sizeClass}${isLoading ? ' wfx-illustration--loading' : ''} ${className}`.trim()}
      style={style}
      role={label && !isLoading ? 'img' : undefined}
      aria-label={label && !isLoading ? label : undefined}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {svgContent ? (
        <div dangerouslySetInnerHTML={{ __html: svgContent }} aria-hidden={label ? undefined : true} />
      ) : null}
    </div>
  );
}
